import {
  BriefcaseBusiness,
  LayoutDashboard,
  Settings as SettingsIcon,
  Users,
} from 'lucide-react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { hasJobBoardPermission } from '../../utils/jobBoardAccess';
import AdminApplicantDetailsPage from './AdminApplicantDetailsPage';
import AdminApplicantsPage from './AdminApplicantsPage';
import AdminJobDetailsPage from './AdminJobDetailsPage';
import AdminJobFormPage from './AdminJobFormPage';
import AdminJobsPage from './AdminJobsPage';
import ApplicantsList from './ApplicantsList';
import ApplicantsOverview from './ApplicantsOverview';
import JobBoardSettingsPage from './JobBoardSettingsPage';
import OverviewDashboard from './OverviewDashboard';

const TABS = [
  {
    path: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    permission: 'view' as const,
  },
  {
    path: 'jobs',
    label: 'Jobs',
    icon: BriefcaseBusiness,
    permission: 'view' as const,
  },
  {
    path: 'applicants',
    label: 'Applicants',
    icon: Users,
    permission: 'view' as const,
  },
  {
    path: 'settings',
    label: 'Configuration',
    icon: SettingsIcon,
    permission: 'manage' as const,
  },
];

export default function JobBoardModule() {
  const { token } = useAuth();
  const location = useLocation();
  const visibleTabs = TABS.filter((tab) => hasJobBoardPermission(token, tab.permission));

  const isTabActive = (path: string) =>
    location.pathname === `/job-board/${path}` ||
    location.pathname.startsWith(`/job-board/${path}/`);

  return (
    <div className="min-h-screen p-6 pt-10 md:p-8 lg:p-10">
      <header className="mb-8 mt-5">
        <h1 className="flex items-center gap-2 text-2xl font-bold leading-tight text-slate-800">
          <BriefcaseBusiness className="text-strivre-burgundy" size={24} />
          Job Board
        </h1>
        <p className="mt-1 text-left text-sm text-slate-500">
          Manage jobs, applicants, publishing, and job board configuration.
        </p>
      </header>

      <nav className="mb-6 w-full overflow-visible">
        <div className="flex flex-wrap gap-1 rounded-2xl border border-strivre-lightnavy bg-strivre-softgrey p-1 shadow-inner md:p-1.5">
          {visibleTabs.map((tab) => (
            <Link
              key={tab.path}
              to={`/job-board/${tab.path}`}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-xs font-bold transition-all lg:flex-none md:text-sm ${
                isTabActive(tab.path)
                  ? 'bg-strivre-burgundy text-white shadow-sm'
                  : 'text-slate-500 hover:text-strivre-burgundy'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </Link>
          ))}
        </div>
      </nav>

      <Routes>
        <Route path="/" element={<Navigate to="dashboard" replace />} />
        <Route path="overview" element={<Navigate to="../dashboard" replace />} />
        {hasJobBoardPermission(token, 'view') && (
          <>
            <Route path="dashboard" element={<OverviewDashboard />} />
            <Route path="jobs" element={<AdminJobsPage />} />
            <Route path="jobs/overview" element={<AdminJobsPage />} />
            <Route path="jobs/:id/applicants" element={<AdminApplicantsPage />} />
            <Route
              path="jobs/:id/applicants/:applicantId"
              element={<AdminApplicantDetailsPage />}
            />
            <Route path="jobs/:id" element={<AdminJobDetailsPage />} />
            <Route path="applicants" element={<ApplicantsOverview />} />
            <Route path="applicants/overview" element={<ApplicantsOverview />} />
            <Route path="applicants/job/:jobId" element={<ApplicantsList />} />
          </>
        )}
        {hasJobBoardPermission(token, 'manage') && (
          <>
            <Route path="jobs/create" element={<AdminJobFormPage />} />
            <Route path="jobs/:id/edit" element={<AdminJobFormPage />} />
            <Route path="settings" element={<JobBoardSettingsPage />} />
          </>
        )}
      </Routes>
    </div>
  );
}
